import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { EnvelopeSimple, Trash, Download, Users, ChartLine, MagnifyingGlass, X, UserMinus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSelection } from "@/hooks/useSelection";
import { useConfirm } from "@/hooks/useConfirm";
import BulkActionBar, { SelectAllCheckbox, RowCheckbox, runBulkAction, BulkBarButton } from "@/components/BulkActionBar";
import { exportRowsToCsv, todayStamp } from "@/lib/csvExport";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const fmtDay = (s) => {
  try {
    return new Date(s).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
  } catch {
    return s;
  }
};

export default function Subscribers() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");

  const filteredList = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) => `${s.email || ""} ${s.nombre || ""}`.toLowerCase().includes(q));
  }, [list, query]);

  const load = useCallback(async () => {
    const [l, s] = await Promise.all([api.get("/subscribers"), api.get("/subscribers/stats")]);
    setList(l.data);
    setStats(s.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onUnsub = async (id) => {
    if (!window.confirm("¿Dar de baja este suscriptor?")) return;
    await api.put(`/subscribers/${id}/baja`);
    toast.success("Suscriptor dado de baja");
    load();
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar permanentemente?")) return;
    await api.delete(`/subscribers/${id}`);
    toast.success("Eliminado");
    load();
  };

  const selection = useSelection(filteredList);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const onBulkDelete = async () => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const ok = await confirm({
      title: `Eliminar ${ids.length} suscriptor${ids.length === 1 ? "" : "es"}`,
      description: "Esta acción es permanente. Los emails desaparecerán y no podrás volver a contactarlos a menos que se vuelvan a suscribir.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkAction(ids, (id) => api.delete(`/subscribers/${id}`));
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} suscriptor(es) eliminado(s)`);
    if (fail) toast.error(`${fail} no se pudieron eliminar`);
    load();
  };

  // Count selected subscribers that are still active — only these can be
  // unsubscribed; the bulk button is hidden when none qualify.
  const activeSelectedIds = () => {
    const sel = new Set(selection.selectedIds);
    return filteredList.filter((s) => sel.has(s.id) && s.status === "activo").map((s) => s.id);
  };

  const onBulkUnsubscribe = async () => {
    const ids = activeSelectedIds();
    if (!ids.length) {
      toast.info("No hay suscriptores activos en la selección.");
      return;
    }
    const ok = await confirm({
      title: `Dar de baja a ${ids.length} suscriptor${ids.length === 1 ? "" : "es"}`,
      description: "Quedarán como 'baja' (sin borrar). Podrás verlos en la lista pero no recibirán correos. Esta acción no es destructiva.",
      confirmLabel: "Dar de baja",
      tone: "default",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkAction(ids, (id) => api.put(`/subscribers/${id}/baja`));
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} suscriptor(es) dado(s) de baja`);
    if (fail) toast.error(`${fail} no se pudieron dar de baja`);
    load();
  };

  const onBulkExport = () => {
    const ids = new Set(selection.selectedIds);
    const rows = filteredList.filter((s) => ids.has(s.id));
    if (!rows.length) return;
    exportRowsToCsv({
      filename: `suscriptores-${todayStamp()}.csv`,
      headers: [
        { key: "email", label: "Email" },
        { key: "nombre", label: "Nombre" },
        { key: "status", label: "Estado" },
        { key: "source_product_id", label: "Producto origen" },
        { key: "source_referral_code", label: "Código referido" },
        {
          label: "Suscrito",
          value: (s) => (typeof s.created_at === "string" ? s.created_at : new Date(s.created_at).toISOString()),
        },
      ],
      rows,
    });
    toast.success(`${rows.length} suscriptor(es) exportado(s) a CSV`);
  };

  const exportCsv = () => {
    const headers = ["email", "nombre", "status", "source_product_id", "source_referral_code", "created_at"];
    const rows = list.map((s) => [
      s.email,
      s.nombre || "",
      s.status,
      s.source_product_id || "",
      s.source_referral_code || "",
      typeof s.created_at === "string" ? s.created_at : new Date(s.created_at).toISOString(),
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r
          .map((v) => {
            const x = String(v ?? "");
            return /[",\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x;
          })
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suscriptores-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = stats || { total: 0, activos: 0, bajas: 0, serie_diaria: [], top_productos: [] };
  const series = (t.serie_diaria || []).map((d) => ({ ...d, label: fmtDay(d.date) }));

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[1400px]" data-testid="subscribers-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
        <div>
          <div className="overline text-klein">Suscriptores</div>
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-2">
            Tu lista propia.
          </h1>
          <p className="text-muted2 mt-3 max-w-xl">
            Cada email es un cliente potencial recurrente. Sin algoritmos de redes sociales de por medio.
          </p>
        </div>
        <button onClick={exportCsv} disabled={list.length === 0} className="btn-ghost" data-testid="btn-export-subs">
          <Download size={16} /> Exportar CSV
        </button>
      </div>

      <section className="grid grid-cols-3 border-t border-l border-zinc-200 mb-10">
        <Kpi label="Total" value={t.total} testid="kpi-sub-total" />
        <Kpi label="Activos" value={t.activos} accent testid="kpi-sub-activos" />
        <Kpi label="Bajas" value={t.bajas} positive={false} testid="kpi-sub-bajas" />
      </section>

      <section className="border border-zinc-200 p-6 mb-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Crecimiento</div>
            <div className="h-display text-2xl font-bold mt-1">Nuevos suscriptores · 30 días</div>
          </div>
          <ChartLine size={22} className="text-klein" />
        </div>
        <div className="h-64">
          {series.every((d) => !d.nuevos) ? (
            <div className="h-full w-full flex items-center justify-center text-sm text-muted2 border border-dashed border-zinc-300 p-6">
              Aún no hay suscriptores. Comparte una página pública para empezar.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#002FA7" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#002FA7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#52525B" }} interval={3} />
                <YAxis tick={{ fontSize: 11, fill: "#52525B" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #09090B", fontSize: 12 }} />
                <Area type="monotone" dataKey="nuevos" stroke="#002FA7" fill="url(#gs)" strokeWidth={2} name="Nuevos" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* Quick search */}
      {list.length > 0 && (
        <div className="mb-4 flex items-center gap-2 max-w-lg" data-testid="subs-search-wrap">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none" />
            <input
              type="text"
              className="input-flat pl-8"
              placeholder="Buscar por email o nombre…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="subs-search"
              data-help="Encuentra un suscriptor rápidamente escribiendo parte de su email o nombre."
            />
          </div>
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="btn-ghost text-xs px-3 py-1.5"
              data-testid="subs-search-clear"
            >
              <X size={14} /> {filteredList.length} de {list.length}
            </button>
          )}
        </div>
      )}

      <BulkActionBar
        count={selection.count}
        noun="suscriptor"
        pluralNoun="suscriptores"
        onDelete={onBulkDelete}
        onClear={selection.clear}
        busy={bulkBusy}
        testid="subs-bulk-bar"
        extraActions={
          <>
            <BulkBarButton
              onClick={onBulkUnsubscribe}
              icon={UserMinus}
              label="Dar de baja"
              testid="subs-bulk-bar-unsub"
              disabled={bulkBusy}
            />
            <BulkBarButton
              onClick={onBulkExport}
              icon={Download}
              label="Exportar CSV"
              testid="subs-bulk-bar-export"
              disabled={bulkBusy}
            />
          </>
        }
      />
      {confirmDialog}

      <section className="border border-zinc-200 hidden lg:block" data-testid="subscribers-table">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
          <Users size={16} className="text-klein" />
          <div className="h-display text-lg font-bold">Lista de suscriptores</div>
        </div>
        {filteredList.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted2">
            <EnvelopeSimple size={28} className="mx-auto mb-3 text-zinc-400" />
            {query ? `No hay suscriptores con "${query}".` : "Aún no hay suscriptores."}
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[11px] uppercase tracking-[0.2em] font-medium text-muted2">
                  <th className="text-left px-4 py-3 w-10">
                    <SelectAllCheckbox
                      allSelected={selection.allSelected}
                      someSelected={selection.someSelected}
                      onChange={selection.toggleAll}
                      testid="subs-select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Nombre</th>
                  <th className="text-left px-4 py-3 w-24">Estado</th>
                  <th className="text-left px-4 py-3 w-28">Suscrito</th>
                  <th className="text-right px-4 py-3 w-20">Acción</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((s) => {
                  const active = s.status === "activo";
                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-zinc-100 hover:bg-zinc-50 ${selection.isSelected(s.id) ? "bg-[#EEF2FF]" : ""}`}
                      style={{ borderLeft: `3px solid ${active ? "#00C853" : "#D4D4D8"}` }}
                      data-testid={`sub-row-${s.id}`}
                    >
                      <td className="px-4 py-3">
                        <RowCheckbox
                          checked={selection.isSelected(s.id)}
                          onChange={() => selection.toggle(s.id)}
                          testid={`select-sub-${s.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{s.email}</td>
                      <td className="px-4 py-3">{s.nombre || "—"}</td>
                      <td className="px-4 py-3">
                        {active ? (
                          <span className="text-[11px] px-2 py-0.5 bg-[#E8F5E9] text-[#1B5E20] border border-[#C8E6C9]">
                            Activo
                          </span>
                        ) : (
                          <span className="text-[11px] px-2 py-0.5 bg-zinc-100 border border-zinc-200">Baja</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted2">
                        {fmtDay(typeof s.created_at === "string" ? s.created_at : new Date(s.created_at).toISOString())}
                      </td>
                      <td className="px-4 py-3 flex justify-end gap-1">
                        {active && (
                          <button
                            onClick={() => onUnsub(s.id)}
                            className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-zinc-300 hover:bg-zinc-100"
                            data-testid={`unsub-${s.id}`}
                          >
                            Baja
                          </button>
                        )}
                        <button
                          onClick={() => onDelete(s.id)}
                          className="p-1.5 hover:bg-zinc-100 text-[#FF2A00]"
                          data-testid={`del-${s.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Mobile / tablet cards */}
      <section className="lg:hidden space-y-3" data-testid="subscribers-mobile-cards">
        {filteredList.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-10 text-center text-sm text-muted2">
            <EnvelopeSimple size={28} className="mx-auto mb-3 text-zinc-400" />
            {query ? `No hay suscriptores con "${query}".` : "Aún no hay suscriptores."}
          </div>
        ) : (
          filteredList.map((s) => {
            const active = s.status === "activo";
            return (
              <div
                key={s.id}
                className={`border border-zinc-200 p-3 ${selection.isSelected(s.id) ? "bg-[#EEF2FF]" : ""}`}
                style={{ borderLeft: `3px solid ${active ? "#00C853" : "#D4D4D8"}` }}
                data-testid={`sub-card-${s.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="shrink-0 pt-0.5">
                    <RowCheckbox
                      checked={selection.isSelected(s.id)}
                      onChange={() => selection.toggle(s.id)}
                      testid={`m-select-sub-${s.id}`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs truncate">{s.email}</div>
                    <div className="text-[11px] text-muted2 truncate mt-0.5">
                      {s.nombre || "Sin nombre"} ·{" "}
                      {fmtDay(typeof s.created_at === "string" ? s.created_at : new Date(s.created_at).toISOString())}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {active && (
                      <button
                        onClick={() => onUnsub(s.id)}
                        className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-zinc-300 hover:bg-zinc-100"
                        data-testid={`m-unsub-${s.id}`}
                      >
                        Baja
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(s.id)}
                      className="p-1.5 hover:bg-zinc-100 text-[#FF2A00]"
                      data-testid={`m-del-${s.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
                <div className="mt-2">
                  {active ? (
                    <span className="text-[10px] px-2 py-0.5 bg-[#E8F5E9] text-[#1B5E20] border border-[#C8E6C9]">
                      Activo
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 bg-zinc-100 border border-zinc-200">Baja</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </section>

      <section className="border border-zinc-200 p-6 mt-6">
        <div className="overline">Por producto</div>
        <div className="h-display text-lg font-bold mt-1 mb-4">Top fuentes</div>
        {(t.top_productos || []).length === 0 ? (
          <div className="text-sm text-muted2">—</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {t.top_productos.map((p, i) => (
              <li key={p.product_id} className="py-3 flex items-center gap-3">
                <div className="font-mono text-muted2 w-5 text-right">{i + 1}</div>
                <div className="flex-1 min-w-0 text-sm truncate">{p.nombre || p.product_id}</div>
                <div className="text-sm font-bold tabular-nums text-klein">{p.subs}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, accent, positive, testid }) {
  return (
    <div className="border-r border-b border-zinc-200 p-6" data-testid={testid}>
      <div className="overline">{label}</div>
      <div
        className={`h-display text-3xl lg:text-4xl font-black tracking-tighter mt-3 ${
          accent ? "text-klein" : positive === false ? "text-[#FF6B35]" : ""
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-muted2 mt-2">{sub}</div>}
    </div>
  );
}
